import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { Redirect } from 'react-router';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { useAuthService } from '../auth.service';
import { IAuthStore } from '../auth.store';
import { ResetPasswordRequestForm } from '../components/ResetPasswordRequestForm';

export const RequestResetPasswordContainer = observer((): ReactElement => {
  const authService = useAuthService();
  const authStore = useInjection(IAuthStore.$);

  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(InputStatus.empty());
  const [externalStatus, setExternalStatus] = useState(InputStatus.empty());
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isRequestDone, setIsRequestDone] = useState(false);

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    setEmailStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
  };

  const validateEmail = (value: string): boolean | undefined => {
    const emailStatus = getEmailStatus(value, [], false);
    setEmailStatus(emailStatus);
    return emailStatus.isValid;
  };

  const handlePasswordResetRequest = async (): Promise<void> => {
    const isEmailValid = validateEmail(email);

    if (!isEmailValid) {
      return;
    }

    setIsRequestPending(true);

    const result = await authService.requestPasswordRequest(email);

    if (result instanceof ACDLError) {
      const errorCodes: TranslationKey[] = result.apiErrorResponse ? result.apiErrorResponse.errorCodes : ['something_went_wrong'];

      setExternalStatus(new InputStatus(false, errorCodes));
      setIsRequestPending(false);
    } else {
      setExternalStatus(new InputStatus(true));
      setIsRequestPending(false);
      setIsRequestDone(true);
    }
  };

  if (authStore.isAuthenticated) {
    return <Redirect to={authService.getAndClearReferrer()} />;
  }

  return (
    <ResetPasswordRequestForm
      handleEmailChange={handleEmailChange}
      validateEmail={validateEmail}
      onRequestPasswordReset={handlePasswordResetRequest}
      emailStatus={emailStatus}
      externalStatus={externalStatus}
      isDisabledSubmit={isRequestPending || isRequestDone}
    />
  );
});
